import { z } from 'zod';
import {
  OUTBOUND_ORDER_STATUSES,
  type OutboundLineStatus,
  type OutboundOrderStatus,
  type PalletStatus,
} from '../enums';
import { idSchema, isoDateTime, optionalText, paginationSchema, requiredText } from './common';
import type { PalletDto } from './pallets';
import { scanCodeSchema } from './pallets';

export const MAX_PALLETS_PER_ORDER = 500;

export const createOutboundOrderSchema = z.object({
  clientId: idSchema,
  palletIds: z
    .array(idSchema)
    .min(1, 'Seleccione al menos un palet')
    .max(MAX_PALLETS_PER_ORDER, `Máximo ${MAX_PALLETS_PER_ORDER} palets por orden`),
  /** Lo que pidió el cliente, tal cual (trazabilidad). */
  clientRequest: optionalText(500),
  notes: optionalText(1000),
});
export type CreateOutboundOrderInput = z.input<typeof createOutboundOrderSchema>;

export const pickPalletSchema = z.object({ palletCode: scanCodeSchema });

export const shipOutboundSchema = z.object({
  /** Líneas cuya salida se confirma (normalmente, las recogidas). */
  lineIds: z.array(idSchema).min(1, 'Indique qué palets salen').max(MAX_PALLETS_PER_ORDER),
  /** Fecha y hora de salida; por defecto, ahora. */
  exitedAt: isoDateTime.optional(),
});
export type ShipOutboundInput = z.input<typeof shipOutboundSchema>;

export const cancelOutboundSchema = z.object({ reason: requiredText(500) });

export const listOutboundQuerySchema = paginationSchema.extend({
  status: z.enum(OUTBOUND_ORDER_STATUSES).optional(),
  clientId: idSchema.optional(),
});
export interface ListOutboundQuery {
  page?: number;
  pageSize?: number;
  status?: OutboundOrderStatus;
  clientId?: number;
}

export const availablePalletsQuerySchema = z.object({
  clientId: idSchema,
  productId: idSchema.optional(),
});

export const resolveReferencesSchema = z.object({
  clientId: idSchema,
  references: z.array(z.string().trim().min(1).max(60)).min(1).max(MAX_PALLETS_PER_ORDER),
});

/** Resultado de buscar las referencias que pide el cliente ("33, 34, 35"). */
export interface ResolvedReference {
  reference: string;
  /** Palets en almacén del cliente con esa referencia (puede haber varios: lote). */
  pallets: PalletDto[];
}

export interface OutboundLineDto {
  id: number;
  palletId: number;
  palletCode: string;
  palletStatus: PalletStatus;
  clientReference: string | null;
  productName: string;
  locationCode: string | null;
  status: OutboundLineStatus;
  pickedAt: string | null;
  pickedByName: string | null;
  shippedAt: string | null;
  shippedByName: string | null;
}

export interface OutboundCounts {
  pending: number;
  picked: number;
  shipped: number;
  removed: number;
}

export interface OutboundOrderSummaryDto {
  id: number;
  clientId: number;
  clientName: string;
  status: OutboundOrderStatus;
  clientRequest: string | null;
  createdAt: string;
  createdByName: string;
  completedAt: string | null;
  counts: OutboundCounts;
}

export interface OutboundOrderDto extends OutboundOrderSummaryDto {
  notes: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  /** Ordenadas por ubicación: es la ruta de recogida. */
  lines: OutboundLineDto[];
}

export interface PickResult {
  line: OutboundLineDto;
  alreadyPicked: boolean;
}
